<template>
  <div
    class="flex flex-column gap-2"
    data-testid="time-off-view-payment"
    v-if="timeOff.status === 2 && hasAnyPermission(['validate_payments', 'process_payments'])"
  >
    <h4 class="m-0" data-testid="time-off-view-payment-title">Approve Payment</h4>
    <p class="m-0">Please mark which of the approved days should be paid.</p>
    <a-table
      data-testid="time-off-view-payment-table"
      :row-selection="{
        selectedRowKeys: dates,
        onChange: onSelectChange,
        getCheckboxProps: record => ({
          props: {
            disabled: !record.shift
          }
        })
      }"
      :rowKey="record => record.id"
      size="small"
      :columns="columns"
      :data-source="timeOff.items"
      :scroll="{ y: 200 }"
    >
      <span slot="status" slot-scope="text, record"
        ><a-tag :color="record.shift ? '#10b981' : '#ef4444'">{{ record.shift ? 'Approved' : 'Declined' }}</a-tag>
      </span>
      <span slot="payment" slot-scope="text, record"
        ><a-tag
          v-if="record.shift"
          :color="getPayCodeReadyForPayStatus(record.shift.internal_payment_info.ready_for_pay_status).color"
          >{{ getPayCodeReadyForPayStatus(record.shift.internal_payment_info.ready_for_pay_status).title }}</a-tag
        >
        <span v-else>-</span>
      </span>

      <template slot="footer">
        <div v-if="dates.length" class="flex justify-content-between" data-testid="time-off-view-payment-footer">
          <span data-testid="time-off-view-payment-selected-count">{{ `Selected ${dates.length} dates` }}</span>
          <a-button type="danger" @click="handleDeleteDays" data-testid="time-off-view-payment-delete-days-button">
            Delete Days
          </a-button>
        </div>
      </template>
    </a-table>
    <signature
      checkbox-label="I've reviewed the time off request and confirm the selected days"
      ref="signatureRef"
      :key="paymentSignatureKey"
      test-id="time-off-view-payment-signature"
      checkbox-wrapper-test-id="time-off-view-payment-signature-checkbox-wrapper"
      checkbox-test-id="time-off-view-payment-signature-checkbox"
      canvas-container-test-id="time-off-view-payment-signature-canvas-container"
      canvas-element-test-id="time-off-view-payment-signature-canvas"
      clear-button-test-id="time-off-view-payment-signature-clear-button"
      undo-button-test-id="time-off-view-payment-signature-undo-button"
      @checked="$emit('update:checked', $event)"
      @save-signature="setSignatureId"
    />
  </div>
</template>

<script>
import Signature from '@/components/signature.vue'
import permissionMixin from '@/mixins/permissionMixin'
import { Button, notification, Table, Tag } from 'ant-design-vue'
import moment from 'moment-timezone'
import { createNamespacedHelpers } from 'vuex'
import shiftsHelper from '@/helpers/shifts'

const { mapState, mapActions } = createNamespacedHelpers('timeOff')
const { mapGetters: shiftGetters } = createNamespacedHelpers('shifts')

export default {
  components: {
    'a-table': Table,
    'a-button': Button,
    'a-tag': Tag,
    Signature
  },
  mixins: [permissionMixin, shiftsHelper],
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    checked: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dates: [],

      columns: [
        {
          title: 'Date',
          key: 'date',
          width: '100px',
          customRender: (_, record) => {
            const { date } = record

            return this.formatDateString(date)
          },
          sorter: (a, b) => {
            const dateA = a.date ? new Date(a.date) : null
            const dateB = b.date ? new Date(b.date) : null

            if (!dateA && !dateB) return 0
            if (!dateA) return 1
            if (!dateB) return -1

            return dateA - dateB
          }
        },
        {
          title: 'Status',
          key: 'status',
          scopedSlots: {
            customRender: 'status'
          },
          sorter: (a, b) => !!b.shift - !!a.shift
        },
        {
          title: 'Payment',
          key: 'payment',
          scopedSlots: {
            customRender: 'payment'
          }
        }
      ],

      signature_id: undefined,

      paymentSignatureKey: 0,
      paymentApprovedSignatureKey: 0
    }
  },
  computed: {
    ...shiftGetters(['getPayCodeReadyForPayStatus']),
    ...mapState({
      timeOff: state => state.timeOff
    }),

    timeZone() {
      return this.$store.state.applicationState.timeZone
    }
  },
  methods: {
    ...mapActions(['approveShiftsTimeOff']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },
    handleDeleteDays() {
      this.$emit('delete', this.dates)
    },

    setSignatureId(signatureId) {
      this.signature_id = signatureId
    },

    onSelectChange(selectedRowKeys) {
      this.dates = selectedRowKeys
    },

    formatDateString(date) {
      return moment.utc(date).tz(this.timeZone).format('MM/DD/YYYY')
    },

    isDisableDate(id) {
      return this.timeOff.conflicts
        .filter(item => item.shift !== null && ['started', 'completed'].includes(item.shift.status))
        .map(item => item.id)
        .includes(id)
    },

    async handleApprove() {
      this.$emit('update:loading', true)

      try {
        await this.$refs.signatureRef.saveSignature()

        if (!this.signature_id) return

        await this.approveShiftsTimeOff({
          items: this.dates,
          id: this.timeOff.id,
          signature_id: this.signature_id
        })
        this.$emit('approve')
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
      } finally {
        this.$emit('update:loading', false)
      }
    }
  },
  mounted() {
    this.paymentSignatureKey += 1
    this.paymentApprovedSignatureKey += 1
  }
}
</script>
